import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SubTabs, useAdminGet } from '../kit';
import type { AdminTargetIn } from '../adminNav';
import {
  Layer, type RecrawlCtx, S, arr, closeTopRecrawlLayer, str, toHealth, toUni, useRecrawlLayer,
} from './shared';
import Overview from './Overview';
import Universities from './Universities';
import UniversityDrawer from './UniversityDrawer';
import RecrawlFlow from './Flow';
import Runs from './Runs';
import RunDrawer from './RunDrawer';
import Changes from './Changes';
import Settings from './Settings';
import Guide from './Guide';

/**
 * /admin → Recrawl: the recrawl engine's own section (owner, 2026-10-25: "add
 * this in admin panel … dedicated section/page … interactive flow like the
 * search flow … full visual timeline for every data we have for univ, overall
 * & in specifics … options to schedule when to rerun, how often … controls").
 * Backend: Backend/recrawl/engine.py (section "recrawl"; shapes in its
 * docstring), flow.py and insights.py; design in recrawl/DESIGN.md.
 *
 *  - Overview: KPIs, what needs a person (attention, runs awaiting approval or
 *    held), what is running and queued, the next scheduled runs and a health
 *    tile per university.
 *  - Universities: every university with its schedule, last / next run and
 *    risk; a drawer per university with its manifest, throttle (declared by
 *    the scripts vs enforced by the governor, with an override editor), the
 *    schedule editor, Probe / Dry run / Run now / Pause, the manual-file
 *    upload, its change timeline, recent runs and request-health trend.
 *  - Flow: one university's pipeline as the Search-flow graph (the search
 *    components, imported unchanged), scenario playback and the replay of a
 *    stored run with its step Gantt.
 *  - Runs: the run history; a drawer per run with steps, counts, guard,
 *    cost, backup, per-step logs (live while it runs), the staged changes and
 *    the approve / reject / cancel controls.
 *  - Changes: added / modified / removed / re-added over time (overall or per
 *    university), the listed-technology curve, a leaderboard and the full
 *    history of one technology.
 *  - Settings: every engine setting with its bounds, the scheduler and its
 *    kill switches, the master / reconcile tools.
 *  - Guide: recrawl/GUIDE.md.
 *
 * The universities list and the request-health rollup are loaded once here
 * and shared (tables, selectors, health dots); any change made from a tab
 * bumps `tick`, and every tab reloads on it.
 *
 * Deep links (admin/adminNav.tsx): /admin?group=recrawl&tab=<overview|
 * universities|flow|runs|changes|settings|guide> plus code=<CODE> (opens the
 * university drawer; on the Flow tab, selects it), run=<id> (opens the run;
 * on the Flow tab, replays it), status=<run status> (Runs), tech=<url or id>
 * (Changes → technology history).
 */

const TABS = ['Overview', 'Universities', 'Flow', 'Runs', 'Changes', 'Settings', 'Guide'];
const TAB_PARAM: Record<string, string> = {
  overview: 'Overview', universities: 'Universities', university: 'Universities', unis: 'Universities',
  flow: 'Flow', pipeline: 'Flow', runs: 'Runs', run: 'Runs', changes: 'Changes', timeline: 'Changes',
  history: 'Changes', settings: 'Settings', scheduler: 'Settings', guide: 'Guide', docs: 'Guide',
};

type Focus<T> = (T & { n: number }) | null;

const RecrawlPanel: React.FC<{ target?: AdminTargetIn }> = ({ target }) => {
  const [tab, setTab] = useState(TABS[0]);
  // Tabs mount on first visit and then stay (hidden), keeping their filters.
  const [seen, setSeen] = useState<Record<string, boolean>>({ [TABS[0]]: true });
  const pick = useCallback((t: string) => { setTab(t); setSeen(v => (v[t] ? v : { ...v, [t]: true })); }, []);

  const [tick, setTick] = useState(0);
  const unis = useAdminGet<any>(S, 'universities', {}, [tick]);
  const health = useAdminGet<any>(S, 'http_health', {}, [tick]);

  const [openUni, setOpenUni] = useState<string | null>(null);
  const [openRun, setOpenRun] = useState<string | null>(null);
  const [flowFocus, setFlowFocus] = useState<Focus<{ code: string; run?: string }>>(null);
  const [runsFocus, setRunsFocus] = useState<Focus<{ code?: string; status?: string }>>(null);
  const [changesFocus, setChangesFocus] = useState<Focus<{ code?: string; tech?: string }>>(null);

  const universities = useMemo(() => arr(unis.data?.universities).map(toUni).filter(u => u.code), [unis.data]);
  const uniByCode = useMemo(() => new Map(universities.map(u => [u.code, u])), [universities]);
  const healthMap = useMemo(() => new Map(arr(health.data?.universities).map(toHealth).filter(h => h.code)
    .map(h => [h.code, h])), [health.data]);

  const ctx: RecrawlCtx = useMemo(() => ({
    universities, uniByCode, health: healthMap, tick,
    reloadAll: () => setTick(t => t + 1),
    openUniversity: (code: string) => { if (code) setOpenUni(str(code).toUpperCase()); },
    openRun: (id: number | string) => { if (str(id)) setOpenRun(str(id)); },
    goFlow: (code: string, runId?: number | string) => {
      setOpenRun(null); setOpenUni(null);
      setFlowFocus(f => ({ code: str(code).toUpperCase(), run: runId !== undefined ? str(runId) : undefined, n: (f?.n || 0) + 1 }));
      pick('Flow');
    },
    goRuns: f => { setOpenRun(null); setOpenUni(null); setRunsFocus(p => ({ ...f, n: (p?.n || 0) + 1 })); pick('Runs'); },
    goChanges: f => { setOpenRun(null); setOpenUni(null); setChangesFocus(p => ({ ...f, n: (p?.n || 0) + 1 })); pick('Changes'); },
    goTab: (t: string) => { setOpenRun(null); setOpenUni(null); pick(TABS.includes(t) ? t : TAB_PARAM[t] || TABS[0]); },
  }), [universities, uniByCode, healthMap, tick, pick]);

  // Escape closes the topmost drawer only: a run opens over its university, and a heatmap drill-down
  // (Changes, or inside the university drawer) can open a university or a run over itself. Every
  // drawer is a layer (shared.tsx useRecrawlLayer); the one latest in the document is on top.
  const uniLayer = useRecrawlLayer(() => setOpenUni(null));
  const runLayer = useRecrawlLayer(() => setOpenRun(null));
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (document.querySelector('[role="dialog"]')) return;     // a confirm modal is open: Escape is not for the drawer
      if (closeTopRecrawlLayer()) e.preventDefault();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Deep link: a tab, its focus, and/or a drawer to open.
  useEffect(() => {
    if (!target) return;
    const raw = str(target.tab);
    const t = TABS.includes(raw) ? raw : TAB_PARAM[raw.toLowerCase()];
    const code = str(target.code || target.university).toUpperCase();
    const run = str(target.run || target.id);
    if (t === 'Flow') {
      setFlowFocus(f => ({ code, run: run || undefined, n: (f?.n || 0) + 1 }));
      pick('Flow');
      return;
    }
    if (t === 'Runs' && (code || target.status)) {
      setRunsFocus(f => ({ code: code || undefined, status: str(target.status) || undefined, n: (f?.n || 0) + 1 }));
    }
    if (t === 'Changes' && (code || target.tech || target.url)) {
      setChangesFocus(f => ({ code: code || undefined, tech: str(target.tech || target.url) || undefined, n: (f?.n || 0) + 1 }));
    }
    if (t) pick(t);
    if (code && t !== 'Runs' && t !== 'Changes') setOpenUni(code);
    if (run) setOpenRun(run);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.nonce]);

  const pane = (t: string, el: React.ReactNode) =>
    seen[t] ? <div key={t} className={tab === t ? 'flex flex-col gap-4 min-w-0' : 'hidden'}>{el}</div> : null;

  return (
    <div className="flex flex-col gap-4 min-w-0">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <SubTabs tabs={TABS} active={tab} onChange={pick} />
        <span className="text-xs text-grayscale-1100">
          {universities.length ? `${universities.length} universities` : unis.loading ? 'Loading universities…' : ''}
          {unis.error && <span className="text-red-700"> · {unis.error}</span>}
          {unis.data?.manifest_error && <span className="text-red-700" title={str(unis.data.manifest_error)}> · manifest problem</span>}
        </span>
      </div>
      {pane('Overview', <Overview ctx={ctx} />)}
      {pane('Universities', <Universities ctx={ctx} loading={unis.loading && !unis.data} error={unis.error} />)}
      {pane('Flow', <RecrawlFlow ctx={ctx} focus={flowFocus} />)}
      {pane('Runs', <Runs ctx={ctx} focus={runsFocus} />)}
      {pane('Changes', <Changes ctx={ctx} focus={changesFocus} />)}
      {pane('Settings', <Settings ctx={ctx} />)}
      {pane('Guide', <Guide />)}

      {openUni && <Layer id={uniLayer}><UniversityDrawer code={openUni} ctx={ctx} onClose={() => setOpenUni(null)} /></Layer>}
      {openRun && <Layer id={runLayer}><RunDrawer id={openRun} ctx={ctx} onClose={() => setOpenRun(null)} /></Layer>}
    </div>
  );
};

export default RecrawlPanel;
